import { extractRelativePath } from "components/Shared/DomainSplit";
import { DGACarouselArrow } from "components/Shared/dga-carousel-arrow/DGACarouselArrow";
import { DGACheckboxInput } from "components/Shared/dga-checkbox-input/DGACheckboxInput";
import IsRtl from "components/Shared/isRtl";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { A11y, Navigation, Pagination } from "swiper";

import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/pagination";

import { VotingOption } from "../Helpers/VotingSectionHelpers";

type VotingImageCardsOptionsProps = {
  options: VotingOption[];
  selectedOptionIds: string[];
  onToggleOption: (optionId: string) => void;
  sectionId: string;
};

type VotingImageCardProps = {
  option: VotingOption;
  isSelected: boolean;
  onSelect: (optionId: string) => void;
};

const VotingImageCard = ({ option, isSelected, onSelect }: VotingImageCardProps) => {
  const { Image: imageField, Title, Description } = option.fields;
  const image = imageField?.value;

  return (
    <button
      type="button"
      onClick={() => onSelect(option.id)}
      className={`flex w-full flex-col gap-6 rounded-2xl border bg-white p-4 text-start transition-colors ${isSelected ? "border-primary-600" : "border-neutral-300"
        }`}
    >
      <div className="relative h-[250px] w-full shrink-0 overflow-hidden rounded-lg">
        {image?.src && (
          <Image
            src={extractRelativePath(image.src)}
            alt={image.alt || Title?.value || ""}
            width={Number(image.width) || 403}
            height={Number(image.height) || 250}
            className="h-full w-full object-cover"
          />
        )}
        <div
          className="absolute top-4 ltr:left-4 rtl:right-4 z-10"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          role="presentation"
        >
          <DGACheckboxInput
            variant="primary"
            size="sm"
            checked={isSelected}
            onChange={() => onSelect(option.id)}
            aria-label={Title?.value}
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        {Title?.value && <h3 className="text-lg font-bold text-neutral-800">{Title.value}</h3>}
        {Description?.value && (
          <p className="text-base text-neutral-800">{Description.value}</p>
        )}
      </div>
    </button>
  );
};

export const VotingImageCardsOptions = ({
  options,
  selectedOptionIds,
  onToggleOption,
  sectionId,
}: VotingImageCardsOptionsProps) => {
  const baseId = `${sectionId}-voting-cards`.replace(/ /g, "-");
  const paginationId = `${baseId}-pagination`;
  const prevId = `${baseId}-prev`;
  const nextId = `${baseId}-next`;

  if (!options.length) return null;

  return (
    <div className="flex w-full max-w-full flex-col">
      <div
        className={`order-3 mt-8 hidden w-fit gap-4 md:flex ${IsRtl() ? "mr-auto flex-row" : "ml-auto flex-row"
          }`}
      >
        <div
          id={prevId}
          className="[&.swiper-button-lock]:hidden [&.swiper-button-disabled_button]:bg-neutral-200 [&.swiper-button-disabled_button]:text-neutral-400 [&.swiper-button-disabled_button]:cursor-default"
        >
          <DGACarouselArrow
            direction={IsRtl() ? "right" : "left"}
            onClick={() => undefined}
            size="large"
            style="primary"
          />
        </div>
        <div
          id={nextId}
          className="[&.swiper-button-lock]:hidden [&.swiper-button-disabled_button]:bg-neutral-200 [&.swiper-button-disabled_button]:text-neutral-400 [&.swiper-button-disabled_button]:cursor-default"
        >
          <DGACarouselArrow
            direction={IsRtl() ? "left" : "right"}
            onClick={() => undefined}
            size="large"
            style="primary"
          />
        </div>
      </div>

      <div
        className={`order-1 w-full max-w-full overflow-hidden ${IsRtl() ? "mr-[5%] md:mr-0" : "ml-[5%] md:ml-0"
          }`}
      >
        <Swiper
          className="mySwiper w-full !overflow-hidden !p-[1px]"
          watchOverflow
          slidesPerView={1.1}
          spaceBetween={16}
          breakpoints={{
            768: { slidesPerView: 2, spaceBetween: 24 },
            1024: { slidesPerView: 3, spaceBetween: 32 },
          }}
          modules={[Pagination, Navigation, A11y]}
          navigation={{
            prevEl: `#${prevId}`,
            nextEl: `#${nextId}`,
          }}
          pagination={{ clickable: true, el: `#${paginationId}` }}
          freeMode
          a11y={{
            enabled: true,
            paginationBulletMessage: "Go to slide {{index}}",
          }}
          dir={IsRtl() ? "rtl" : "ltr"}
          onSwiper={(swiper) => {
            swiper.navigation.init();
            swiper.navigation.update();
          }}
        >
          {options.map((option) => (
            <SwiperSlide key={option.id} className="!h-auto">
              <VotingImageCard
                option={option}
                isSelected={selectedOptionIds.includes(option.id)}
                onSelect={onToggleOption}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      <div
        id={paginationId}
        className="custom_swiper order-2 !mt-8 mx-auto !justify-center md:hidden"
      />
    </div>
  );
};
